/**
 * Picker de proyectos de Plane. Lista los proyectos del workspace global
 * configurado por env y permite buscar por nombre o identifier.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Check, AlertCircle } from "lucide-react";
import clsx from "clsx";
import { api, ApiError } from "@/lib/api";

interface PlaneProject {
  id: string;
  name: string;
  identifier: string;
  description: string | null;
}

export function PlaneProjectPicker({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (id: string | undefined) => void;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const { data, error } = useQuery<{ projects: PlaneProject[] }>({
    queryKey: ["integrations", "plane", "projects"],
    queryFn: () => api.get("/integrations/plane/projects"),
    staleTime: 5 * 60_000,
  });

  const projects = useMemo(() => data?.projects ?? [], [data]);
  const selected = projects.find((p) => p.id === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects.slice(0, 10);
    return projects
      .filter((p) => p.name.toLowerCase().includes(q) || p.identifier.toLowerCase().includes(q))
      .slice(0, 10);
  }, [projects, query]);

  if (error instanceof ApiError && error.status === 503) {
    return (
      <div className="rounded-md bg-base-200/60 px-3 py-2 text-xs text-base-content/65 flex items-start gap-2">
        <AlertCircle className="size-3.5 mt-0.5 shrink-0 text-warning" />
        <span>
          Plane no configurado. Define <code className="font-mono">PLANE_API_TOKEN</code> y{" "}
          <code className="font-mono">PLANE_WORKSPACE_SLUG</code> en wrangler para usar el picker, o
          pega el ID directo si ya lo conoces.
        </span>
      </div>
    );
  }

  return (
    <div className="relative">
      {selected ? (
        <div className="card-elevated p-3 flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate">
              {selected.name}{" "}
              <span className="text-base-content/55 font-mono text-xs">{selected.identifier}</span>
            </div>
            {selected.description && (
              <div className="text-xs text-base-content/55 truncate mt-0.5">
                {selected.description}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              onChange(undefined);
              setQuery("");
            }}
            className="btn btn-ghost btn-xs"
          >
            Cambiar
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 150)}
              placeholder="Buscar proyecto en Plane"
              className="input input-bordered w-full pl-9"
            />
          </div>
          {open && (
            <div className="absolute left-0 right-0 top-full mt-1 z-30 card-elevated max-h-64 overflow-y-auto p-1">
              {filtered.length === 0 ? (
                <div className="px-3 py-3 text-sm text-base-content/55 italic">
                  {query
                    ? "Sin coincidencias."
                    : projects.length === 0
                      ? "No hay proyectos en el workspace."
                      : "Empieza a escribir para buscar."}
                </div>
              ) : (
                filtered.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onChange(p.id);
                      setQuery("");
                      setOpen(false);
                    }}
                    className={clsx(
                      "w-full px-3 py-2 text-left rounded-md hover:bg-base-200 transition-colors flex items-center justify-between gap-2",
                      p.id === value && "bg-base-200",
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">
                        {p.name}{" "}
                        <span className="text-base-content/45 font-mono text-xs">
                          {p.identifier}
                        </span>
                      </div>
                      {p.description && (
                        <div className="text-xs text-base-content/55 truncate">{p.description}</div>
                      )}
                    </div>
                    {p.id === value && <Check className="size-3.5 text-primary shrink-0" />}
                  </button>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
